import React, { useEffect, useState } from 'react';
import api from '../api';

// Super admin: platform-wide change log (who changed what, and when).
// Entries come from the auditTrail middleware plus explicit impersonation logs.

const ACTION_STYLE = {
  created:     { label: 'Created',      color: 'var(--success)',       bg: 'rgba(34,197,94,0.12)' },
  updated:     { label: 'Updated',      color: 'var(--primary-light)', bg: 'rgba(99,102,241,0.12)' },
  deleted:     { label: 'Deleted',      color: 'var(--danger)',        bg: 'rgba(239,68,68,0.12)' },
  impersonate: { label: 'Logged in as', color: 'var(--warning)',       bg: 'rgba(245,158,11,0.12)' },
};

export default function AdminAudit() {
  const [days, setDays] = useState(7);
  const [action, setAction] = useState('');
  const [entity, setEntity] = useState('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState(''); // debounced q
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setSearch(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/admin/audit', { params: { days, action, entity, q: search } })
      .then(r => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days, action, entity, search]);

  const logs = data?.logs || [];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Audit Trail</h1>
        <div style={{ display: 'flex', gap: 6 }}>
          {[1, 7, 30, 90].map(d => (
            <button key={d}
              className={`btn btn-sm ${days === d ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setDays(d)}>
              {d === 1 ? '24h' : `${d} days`}
            </button>
          ))}
        </div>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: -6, marginBottom: 16 }}>
        Every change made on the platform — creates, updates, deletes and admin logins as users.
        Secrets are redacted before logging; entries are kept for 180 days.
      </p>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          className="input"
          style={{ maxWidth: 320 }}
          placeholder="Search by user, email, or record id…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <select className="input" style={{ maxWidth: 170 }} value={action} onChange={e => setAction(e.target.value)}>
          <option value="">All actions</option>
          <option value="created">Created</option>
          <option value="updated">Updated</option>
          <option value="deleted">Deleted</option>
          <option value="impersonate">Logged in as</option>
        </select>
        <select className="input" style={{ maxWidth: 200 }} value={entity} onChange={e => setEntity(e.target.value)}>
          <option value="">All areas</option>
          {(data?.entities || []).map(en => <option key={en} value={en}>{en}</option>)}
        </select>
        {data && (
          <span style={{ alignSelf: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            {data.total.toLocaleString()} event{data.total === 1 ? '' : 's'}
            {data.total > logs.length ? ` (showing latest ${logs.length})` : ''}
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading audit trail…</div>
      ) : logs.length === 0 ? (
        <div className="card" style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>
          No events match this filter.
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>When</th><th>User</th><th>Action</th><th>What</th><th>Details</th></tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const st = ACTION_STYLE[log.action] || { label: log.action, color: 'var(--text-muted)', bg: 'var(--surface2)' };
                  return (
                    <tr key={log._id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>
                          {log.actor_name}
                          {['admin', 'super_admin'].includes(log.actor_role) && (
                            <span style={{ fontSize: 10.5, color: 'var(--warning)', marginLeft: 6 }}>
                              {log.actor_role === 'super_admin' ? 'SUPER ADMIN' : 'ADMIN'}
                            </span>
                          )}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{log.actor_email}</div>
                      </td>
                      <td>
                        <span style={{
                          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                          color: st.color, background: st.bg, whiteSpace: 'nowrap',
                        }}>{st.label}</span>
                      </td>
                      <td>
                        {log.action === 'impersonate' ? (
                          <div>
                            <div style={{ fontWeight: 500 }}>{log.target_name}</div>
                            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{log.target_email}</div>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontWeight: 500 }}>{log.entity}</div>
                            <div style={{ color: 'var(--text-muted)', fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                              {log.method} {log.path}
                            </div>
                          </div>
                        )}
                      </td>
                      <td style={{ maxWidth: 340 }}>
                        {log.body ? (
                          <details>
                            <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
                              {Object.keys(log.body).slice(0, 4).join(', ')}{Object.keys(log.body).length > 4 ? '…' : ''}
                            </summary>
                            <pre style={{
                              fontSize: 11, background: 'var(--surface2)', borderRadius: 8, padding: 10,
                              marginTop: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 240, overflow: 'auto',
                            }}>{JSON.stringify(log.body, null, 2)}</pre>
                          </details>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
